export interface LabelMatch {
	readonly segmentIndex: number;
	readonly start: number;
	readonly end: number;
}

export type LabelRunState = "plain" | "match" | "active";

export interface LabelHighlightRun {
	readonly text: string;
	readonly state: LabelRunState;
}

/** ラベル配列から重複しない検索一致箇所を順番どおりに返す。 */
export function findLabelMatches(
	labels: readonly string[],
	query: string,
	caseSensitive: boolean,
): LabelMatch[] {
	if (query.length === 0) return [];

	const needle = caseSensitive ? query : query.toLowerCase();
	const matches: LabelMatch[] = [];
	for (const [segmentIndex, label] of labels.entries()) {
		const haystack = caseSensitive ? label : label.toLowerCase();
		let searchStart = 0;
		while (searchStart <= haystack.length - needle.length) {
			const start = haystack.indexOf(needle, searchStart);
			if (start === -1) break;
			const end = start + needle.length;
			matches.push({ segmentIndex, start, end });
			searchStart = end;
		}
	}
	return matches;
}

/** 一致箇所を segmentIndex ごとにグルーピングする。 */
export function groupMatchesBySegment(matches: readonly LabelMatch[]): Map<number, LabelMatch[]> {
	const grouped = new Map<number, LabelMatch[]>();
	for (const match of matches) {
		const segmentMatches = grouped.get(match.segmentIndex) ?? [];
		segmentMatches.push(match);
		grouped.set(match.segmentIndex, segmentMatches);
	}
	return grouped;
}

/** 指定された 1 件の一致箇所だけを置換したラベル配列を返す。 */
export function replaceLabelMatch(
	labels: readonly string[],
	match: LabelMatch,
	replacement: string,
): string[] {
	if (match.segmentIndex < 0 || match.segmentIndex >= labels.length) return [...labels];
	const label = labels[match.segmentIndex];
	if (match.start < 0 || match.end < match.start || match.end > label.length) return [...labels];

	const next = [...labels];
	next[match.segmentIndex] = label.slice(0, match.start) + replacement + label.slice(match.end);
	return next;
}

/** 与えられた一致箇所 (同一ラベル配列に対して求めたもの) をすべて置換した結果を返す。 */
export function replaceAllLabelMatches(
	labels: readonly string[],
	matches: readonly LabelMatch[],
	replacement: string,
): string[] {
	if (matches.length === 0) return [...labels];
	const matchesBySegment = groupMatchesBySegment(matches);

	return labels.map((label, segmentIndex) => {
		const segmentMatches = matchesBySegment.get(segmentIndex);
		if (!segmentMatches) return label;

		let cursor = 0;
		let result = "";
		for (const match of segmentMatches) {
			result += label.slice(cursor, match.start) + replacement;
			cursor = match.end;
		}
		return result + label.slice(cursor);
	});
}

/** ラベル本文を通常文字・一致・現在選択中の一致の 3 状態へ分割する。 */
export function toLabelHighlightRuns(
	label: string,
	matches: readonly LabelMatch[],
	activeMatch: LabelMatch | null,
): LabelHighlightRun[] {
	if (label.length === 0) return [];

	const runs: LabelHighlightRun[] = [];
	let cursor = 0;
	for (const match of matches) {
		const start = Math.max(cursor, match.start);
		const end = Math.min(label.length, match.end);
		if (end <= start) continue;
		if (start > cursor) runs.push({ text: label.slice(cursor, start), state: "plain" });
		const active =
			activeMatch !== null &&
			activeMatch.segmentIndex === match.segmentIndex &&
			activeMatch.start === match.start &&
			activeMatch.end === match.end;
		runs.push({ text: label.slice(start, end), state: active ? "active" : "match" });
		cursor = end;
	}
	if (cursor < label.length) runs.push({ text: label.slice(cursor), state: "plain" });
	return runs;
}
